import React, { useState, useEffect, useMemo } from 'react';
import { Spinner, Modal, ModalHeader, ModalBody } from 'reactstrap';
import { toast } from 'react-toastify';
import { loadTransactions, groupByCourse, sumRemainingClasses, sumRemainingValue, combineTransactions } from './txnChangeShared';
import CourseGroupList from './CourseGroupList';
import CourseChangePanel from './course-change-panel';
import LevelChangePanel from './level-change-panel';
import CourseTransferPanel from './course-transfer-panel';

interface Props {
  studentId: string;
  profileData: any;
  userType?: string;
}

type Action = 'course' | 'level' | 'transfer';

const ACTIONS: { key: Action; label: string }[] = [
  { key: 'course', label: 'Change Course' },
  { key: 'level', label: 'Change Level' },
  { key: 'transfer', label: 'Transfer to Student' },
];

const actionButtonStyle = (isActive: boolean): React.CSSProperties => ({
  backgroundColor: isActive ? '#7a70b3' : '#f1f1f4',
  color: isActive ? 'white' : '#495057',
  border: 'none',
  borderRadius: '20px',
  padding: '0.4rem 1rem',
  cursor: 'pointer',
  marginRight: '0.5rem',
  fontSize: '0.9rem',
});

// The single transaction-based change screen. Transactions are loaded ONCE and shown grouped
// by course; the admin ticks the transactions of one course and opens a popup to change them
// together (Change Course / Change Level / Transfer to Student). Switching action re-mounts the panel (via key) so
// its form resets; the list is reloaded only after a change succeeds.
const TransactionChange: React.FC<Props> = ({ studentId, profileData, userType }) => {
  const [loading, setLoading] = useState(false);
  const [transactionList, setTransactionList] = useState<any[]>([]);
  const [selectedTransactions, setSelectedTransactions] = useState<any[] | null>(null);
  const [action, setAction] = useState<Action>('course');

  useEffect(() => {
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const init = async () => {
    setLoading(true);
    try {
      setTransactionList(await loadTransactions(studentId));
    } catch (error) {
      console.error(error);
      toast.error('Failed to load transactions');
    } finally {
      setLoading(false);
    }
  };

  const groups = useMemo(() => groupByCourse(transactionList), [transactionList]);

  const openSelection = (transactions: any[]) => {
    setSelectedTransactions(transactions);
    setAction('course'); // default action each time a selection is opened
  };

  const closeModal = () => setSelectedTransactions(null);

  // Called by a panel after a successful change: close the popup and refresh the list.
  const handleDone = () => {
    setSelectedTransactions(null);
    init();
  };

  const selectionKey = (selectedTransactions || []).map((t) => t.order_id || t.id).join(',');
  const selectionView = combineTransactions(selectedTransactions || []);
  const panelProps = {
    transactions: selectedTransactions || [],
    studentId,
    profileData,
    userType,
    onDone: handleDone,
  };

  if (loading) {
    return <div className="d-flex justify-content-center"><Spinner color="primary" /></div>;
  }

  return (
    <div>
      <h5 style={{ marginBottom: 4 }}>Course Change</h5>
      <p style={{ color: '#6c757d', marginBottom: 8 }}>
        Student: <b>{[profileData?.firstName, profileData?.lastName].filter(Boolean).join(' ') || '-'}</b> (ID {studentId})
        {' · '}Tick the transactions to change, then use <b>Change this course</b>. All their remaining classes move together.
      </p>

      <CourseGroupList groups={groups} onChange={openSelection} />

      {/* Popup: choose the action, then complete it */}
      <Modal isOpen={!!selectedTransactions} toggle={closeModal} size="lg" scrollable>
        <ModalHeader toggle={closeModal}>
          {selectionView.courseList?.[0]?.courseName || 'Transaction'}
          <span style={{ display: 'block', fontSize: '0.85rem', fontWeight: 400, color: '#6c757d' }}>
            {selectedTransactions?.length} transaction{(selectedTransactions?.length || 0) > 1 ? 's' : ''} ·{' '}
            {sumRemainingClasses(selectionView)} classes · {sumRemainingValue(selectionView).toFixed(2)} {selectionView.currency}
          </span>
        </ModalHeader>
        <ModalBody>
          <div style={{ marginBottom: 16 }}>
            {ACTIONS.map((a) => (
              <button
                key={a.key}
                type="button"
                style={actionButtonStyle(action === a.key)}
                onClick={() => setAction(a.key)}
              >
                {a.label}
              </button>
            ))}
          </div>

          {selectedTransactions && action === 'course' && (
            <CourseChangePanel key={`course-${selectionKey}`} {...panelProps} />
          )}
          {selectedTransactions && action === 'level' && (
            <LevelChangePanel key={`level-${selectionKey}`} {...panelProps} />
          )}
          {selectedTransactions && action === 'transfer' && (
            <CourseTransferPanel key={`transfer-${selectionKey}`} {...panelProps} />
          )}
        </ModalBody>
      </Modal>
    </div>
  );
};

export default TransactionChange;
